import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Card, CardContent, CardActions,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Chip, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Tabs, Tab, IconButton, Alert, CircularProgress,
  Accordion, AccordionSummary, AccordionDetails, Switch, FormControlLabel,
  LinearProgress, Badge, List, ListItem, ListItemText, ListItemIcon,
  Divider, Tooltip, Autocomplete, RadioGroup, Radio, FormGroup, Checkbox,
  Skeleton
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Send as SendIcon, Visibility as ViewIcon, Assignment as AssignIcon,
  CheckCircle as SignedIcon, Pending as PendingIcon, Cancel as DeclinedIcon,
  NotificationsActive as RemindIcon, ExpandMore as ExpandMoreIcon,
  Business as CompanyIcon, Group as TeamIcon, Person as UserIcon,
  Analytics as AnalyticsIcon, History as HistoryIcon,
  Description as ContractIcon, Dashboard as DashboardIcon,
  Star as CreatorIcon, Payment as BlitzPayIcon, PersonAdd as PersonAddIcon,
  Email as EmailIcon, ContentCopy as CopyIcon, Public as PublicIcon
} from '@mui/icons-material';
import client from "../../../../API";
import EnhancedContractTemplateDialog from './contractBuilderAdmin';
import LinkIcon from '@mui/icons-material/Link';

const ContractDetailsView = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [systemStats, setSystemStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createTemplateDialogOpen, setCreateTemplateDialogOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [blitzPayDialogOpen, setBlitzPayDialogOpen] = useState(false);
  const [externalCreateDialogOpen, setExternalCreateDialogOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [externalSignatures, setExternalSignatures] = useState([]);
  const [externalStats, setExternalStats] = useState(null);
  const [searchEmail, setSearchEmail] = useState('');
  
  // NEW: Signed RPA state
  const [signedRPAs, setSignedRPAs] = useState({ creators: [], summary: {} });
  const [rpaStats, setRPAStats] = useState(null);
  const [loadingRPAs, setLoadingRPAs] = useState(false);
  const [rpaFilters, setRPAFilters] = useState({
    include_internal: true,
    include_external: true,
    creator_username: '',
    limit: 100,
    offset: 0
  });

  useEffect(() => {
    fetchOverviewData();
    fetchTemplates();
  }, []);

  const fetchOverviewData = async () => {
    try {
      setLoading(true);
      const stats = await client.contracts.getSystemStats();
      setSystemStats(stats.stats);
    } catch (error) {
      console.error('Failed to fetch contract overview:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const data = await client.contracts.getTemplates();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchExternalSignatures = async (params = {}) => {
    try {
      const data = await client.contracts.getExternalSignatures(params);
      setExternalSignatures(data.external_signatures || []);
    } catch (error) {
      console.error('Failed to fetch external signatures:', error);
    }
  };

  const fetchExternalStats = async () => {
    try {
      const data = await client.contracts.getExternalSignatureStats();
      setExternalStats(data.stats);
    } catch (error) {
      console.error('Failed to fetch external stats:', error);
    }
  };

  const fetchExternalByEmail = async (email) => {
    try {
      const data = await client.contracts.getExternalSignaturesByEmail(email);
      setExternalSignatures(data.external_signatures || []);
    } catch (error) {
      console.error('Failed to fetch external signatures by email:', error);
    }
  };

  // NEW: Fetch signed RPAs
  const fetchSignedRPAs = async () => {
    try {
      setLoadingRPAs(true);
      const data = await client.contracts.blitzpayRpa.getSignedRPAs(rpaFilters);
      setSignedRPAs(data);
    } catch (error) {
      console.error('Failed to fetch signed RPAs:', error);
    } finally {
      setLoadingRPAs(false);
    }
  };

  const fetchRPAStats = async () => {
    try {
      const data = await client.contracts.blitzpayRpa.getSignedStats();
      setRPAStats(data.stats);
    } catch (error) {
      console.error('Failed to fetch RPA stats:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 5) {
      fetchExternalSignatures();
      fetchExternalStats();
    } else if (activeTab === 6) {
      fetchSignedRPAs();
      fetchRPAStats();
    }
  }, [activeTab, rpaFilters]);

  const handleTabChange = (_, newValue) => setActiveTab(newValue);

  const handleCreateTemplate = () => {
    setEditTemplate(null);
    setCreateTemplateDialogOpen(true);
  };
  const handleAssignContract = () => setAssignDialogOpen(true);
  const handleAssignBlitzPay = () => setBlitzPayDialogOpen(true);
  const handleCreateExternal = () => setExternalCreateDialogOpen(true);
  const handleViewAnalytics = () => setActiveTab(3);
  const handleEditTemplate = (template) => {
    setEditTemplate(template);
    setCreateTemplateDialogOpen(true);
  };
  const handleCloseTemplateDialog = () => {
    setCreateTemplateDialogOpen(false);
    setEditTemplate(null);
  };
  const handleSaveTemplate = () => {
    fetchTemplates();
    fetchOverviewData();
  };
  const handleCloseAssignDialog = () => setAssignDialogOpen(false);
  const handleCloseBlitzPayDialog = () => setBlitzPayDialogOpen(false);
  const handleCloseExternalDialog = () => setExternalCreateDialogOpen(false);
  const handleAssignSuccess = () => fetchOverviewData();
  const handleExternalSuccess = () => {
    fetchExternalSignatures();
    fetchExternalStats();
  };
  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url);
  };
  const handleSearchByEmail = () => {
    if (searchEmail) {
      fetchExternalByEmail(searchEmail);
    } else {
      fetchExternalSignatures();
    }
  };

  const tabs = [
    { label: 'Overview', icon: <DashboardIcon /> },
    { label: 'Templates', icon: <ContractIcon /> },
    { label: 'Assignments', icon: <SendIcon /> },
    { label: 'Analytics', icon: <AnalyticsIcon /> },
    { label: 'Pending Signatures', icon: <PendingIcon /> },
    { label: 'External Contracts', icon: <PublicIcon /> },
    { label: 'Signed BlitzPay RPAs', icon: <BlitzPayIcon /> } // NEW TAB
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Contract Management</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage contract templates, assignments, and monitor signature progress
      </Typography>

      {systemStats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">Total Templates</Typography>
              <Typography variant="h4">{systemStats.total_templates}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">Active Templates</Typography>
              <Typography variant="h4" color="success.main">{systemStats.active_templates}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">Total Assignments</Typography>
              <Typography variant="h4">{systemStats.total_assignments}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">Completion Rate</Typography>
              <Typography variant="h4" color="primary.main">
                {systemStats.completion_rate?.toFixed(1)}%
              </Typography>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          {tabs.map((tab, i) => (
            <Tab key={i} label={tab.label} icon={tab.icon} iconPosition="start" />
          ))}
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <ContractOverview 
          onCreateTemplate={handleCreateTemplate}
          onAssignContract={handleAssignContract}
          onAssignBlitzPay={handleAssignBlitzPay}
          onViewAnalytics={handleViewAnalytics}
          onCreateExternal={handleCreateExternal}
        />
      )}
      {activeTab === 1 && (
        <ContractTemplatesAdmin 
          onCreateTemplate={handleCreateTemplate}
          onEditTemplate={handleEditTemplate}
          templates={templates}
          onTemplatesChange={fetchTemplates}
        />
      )}
      {activeTab === 2 && (
        <ContractAssignmentsAdmin 
          onAssignContract={handleAssignContract}
          onAssignBlitzPay={handleAssignBlitzPay}
          templates={templates}
        />
      )}
      {activeTab === 3 && <ContractAnalyticsAdmin />}
      {activeTab === 4 && <PendingSignaturesAdmin />}
      {activeTab === 5 && (
        <ExternalContractsAdmin 
          templates={templates}
          externalSignatures={externalSignatures}
          externalStats={externalStats}
          onCreateExternal={handleCreateExternal}
          onAssignBlitzPay={handleAssignBlitzPay}
          searchEmail={searchEmail}
          onSearchEmailChange={(e) => setSearchEmail(e.target.value)}
          onSearchSubmit={handleSearchByEmail}
          onCopyUrl={handleCopyUrl}
          loading={loading}
        />
      )}
      {activeTab === 6 && (
        <SignedBlitzPayRPAs 
          signedRPAs={signedRPAs}
          rpaStats={rpaStats}
          filters={rpaFilters}
          onFiltersChange={setRPAFilters}
          loading={loadingRPAs}
          onRefresh={fetchSignedRPAs}
        />
      )}

      <EnhancedContractTemplateDialog
        open={createTemplateDialogOpen}
        template={editTemplate}
        onClose={handleCloseTemplateDialog}
        onSave={handleSaveTemplate}
      />
      
      <ContractAssignmentDialog
        open={assignDialogOpen}
        templates={templates}
        onClose={handleCloseAssignDialog}
        onAssign={handleAssignSuccess}
      />

      <BlitzPayTermsDialog
        open={blitzPayDialogOpen}
        templates={templates}
        onClose={handleCloseBlitzPayDialog}
        onAssign={handleAssignSuccess}
      />

      <ExternalSignatureDialog
        open={externalCreateDialogOpen}
        templates={templates}
        onClose={handleCloseExternalDialog}
        onSubmit={handleExternalSuccess}
      />
    </Box>
  );
};

const ContractOverview = ({ onCreateTemplate, onAssignContract, onAssignBlitzPay, onViewAnalytics, onCreateExternal }) => {
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchOverviewData(); }, []);
  const fetchOverviewData = async () => {
    try {
      const auditLogs = await client.contracts.getAuditLogs({ page: 1, per_page: 10 });
      setRecentActivity(auditLogs.audit_logs || []);
    } catch (error) {
      console.error('Failed to fetch overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <Card><CardContent>
          <Typography variant="h6" gutterBottom>Recent Contract Activity</Typography>
          {recentActivity.length > 0 ? (
            <List>
              {recentActivity.slice(0, 10).map((activity) => (
                <ListItem key={activity.id}>
                  <ListItemIcon>
                    {activity.action.includes('signed') ? <SignedIcon color="success" /> :
                     activity.action.includes('declined') ? <DeclinedIcon color="error" /> :
                     activity.action.includes('assigned') ? <SendIcon color="primary" /> :
                     activity.action.includes('creator') ? <CreatorIcon color="secondary" /> :
                     activity.action.includes('blitzpay') ? <BlitzPayIcon color="info" /> :
                     <ContractIcon />}
                  </ListItemIcon>
                  <ListItemText
                    primary={activity.action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    secondary={new Date(activity.created_at).toLocaleString()}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Box textAlign="center" py={4}>
              <ContractIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">No recent activity</Typography>
              <Typography variant="body2" color="text.secondary">Create your first template to get started</Typography>
            </Box>
          )}
        </CardContent></Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card><CardContent>
          <Typography variant="h6" gutterBottom>Quick Actions</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} fullWidth onClick={onCreateTemplate}>
              Create Template
            </Button>
            <Button variant="outlined" startIcon={<SendIcon />} fullWidth onClick={onAssignContract}>
              Assign Contract 
            </Button>
            <Button variant="outlined" startIcon={<BlitzPayIcon />} fullWidth onClick={onAssignBlitzPay}>
              Assign BlitzPay Terms
            </Button>
            <Button variant="outlined" startIcon={<PublicIcon />} fullWidth onClick={onCreateExternal}>
              Create External Signature
            </Button>
            <Button variant="outlined" startIcon={<AnalyticsIcon />} fullWidth onClick={onViewAnalytics}>
              View Analytics
            </Button>
          </Box>
        </CardContent></Card>
        <Card sx={{ mt: 2 }}><CardContent>
          <Typography variant="h6" gutterBottom>Today's Summary</Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><PendingIcon color="warning" /></ListItemIcon>
              <ListItemText primary="Pending Signatures" secondary="Check assignments needing attention" />
            </ListItem>
            <ListItem>
              <ListItemIcon><SignedIcon color="success" /></ListItemIcon>
              <ListItemText primary="Recent Completions" secondary="View latest signed contracts" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CreatorIcon color="secondary" /></ListItemIcon>
              <ListItemText primary="Creator Contracts" secondary="Manage creator agreements" />
            </ListItem>
            </List>
          </CardContent></Card>
        </Grid>
      </Grid>
    );
};

const ContractTemplatesAdmin = ({ onCreateTemplate, onEditTemplate, templates, onTemplatesChange }) => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (templates.length === 0) fetchTemplates(); else setLoading(false);
  }, [templates]);

  const fetchTemplates = async () => {
    try {
      await client.contracts.getTemplates();
      onTemplatesChange && onTemplatesChange();
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await client.contracts.deleteTemplate(id);
      await fetchTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      alert('Failed to delete template. It may have active assignments.');
    }
  };

  const toggleTemplateStatus = async (template) => {
    try {
      const newStatus = template.status === 'active' ? 'archived' : 'active';
      await client.contracts.updateTemplate(template.id, { status: newStatus });
      await fetchTemplates();
    } catch (error) {
      console.error('Failed to update template status:', error);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Contract Templates ({templates.length})</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={onCreateTemplate}>Create Template</Button>
      </Box>
      {templates.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead><TableRow>
              <TableCell>Name</TableCell><TableCell>Type</TableCell><TableCell>Status</TableCell><TableCell>Version</TableCell>
              <TableCell>Assignments</TableCell><TableCell>Created</TableCell><TableCell>Actions</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {templates.map(t => (
                <TableRow key={t.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">{t.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{t.description}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={t.contract_type} 
                      color={t.contract_type === 'simple' ? 'info' : 'default'} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip label={t.status} color={t.status === 'active' ? 'success' : 'default'} size="small" />
                  </TableCell>
                  <TableCell>v{t.version}</TableCell>
                  <TableCell>
                    <Badge badgeContent={t.assignment_count} color="primary"><AssignIcon /></Badge>
                  </TableCell>
                  <TableCell>{new Date(t.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => onEditTemplate(t)}><EditIcon /></IconButton>
                      </Tooltip>
                      <Tooltip title={t.status === 'active' ? 'Archive' : 'Activate'}>
                        <IconButton size="small" onClick={() => toggleTemplateStatus(t)}>
                          {t.status === 'active' ? '📦' : '✅'}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteTemplate(t.id)}
                          disabled={t.assignment_count > 0}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Card><CardContent>
          <Box textAlign="center" py={8}>
            <ContractIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" gutterBottom>No Templates Yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first contract template to get started with digital signatures
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={onCreateTemplate}>Create Your First Template</Button>
          </Box>
        </CardContent></Card>
      )}
    </Box>
  );
};

const ContractAssignmentsAdmin = ({ onAssignContract, onAssignBlitzPay, templates }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  const BLITZ_ROOT = 'https://www.useblitz.co';
  const buildContractLink = (assignmentId) => `${BLITZ_ROOT}/contract/${assignmentId}`;
  const handleOpenPublicLink = (assignmentId) => window.open(buildContractLink(assignmentId), '_blank', 'noopener');

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    try {
      const assignmentsData = await client.contracts.getAssignments();
      setAssignments(assignmentsData.assignments || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminders = async (assignmentId) => {
    try {
      const result = await client.contracts.sendReminders(assignmentId);
      alert(`Sent ${result.reminder_count} reminders successfully`);
      fetchData();
    } catch (error) {
      console.error('Failed to send reminders:', error);
      alert('Failed to send reminders');
    }
  };

  const getScopeIcon = (scope) => {
    switch (scope) {
      case 'company': return <CompanyIcon />;
      case 'team': return <TeamIcon />;
      case 'user': return <UserIcon />;
      case 'creators_all':
      case 'creators_specific': return <CreatorIcon />;
      default: return <AssignIcon />;
    }
  };

  const getScopeLabel = (scope) => {
    switch (scope) {
      case 'creators_all': return 'All Creators';
      case 'creators_specific': return 'Specific Creators';
      default: return scope;
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Contract Assignments ({assignments.length})</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            startIcon={<AssignIcon />}
            onClick={onAssignContract}
            disabled={templates.filter(t => t.status === 'active').length === 0}
          >
            Assign Contract
          </Button>
          <Button
            variant="outlined"
            startIcon={<BlitzPayIcon />}
            onClick={onAssignBlitzPay}
            disabled={templates.filter(t => t.status === 'active').length === 0}
          >
            BlitzPay Terms
          </Button>
        </Box>
      </Box>

      {assignments.length > 0 ? (
        assignments.map(a => (
          <Accordion key={a.id}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" width="100%">
                <Box flex={1}>
                  <Typography variant="subtitle1" fontWeight="medium">{a.template?.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getScopeLabel(a.assignment_scope)} • {a.signature_stats?.total} signatures
                  </Typography>
                </Box>
                <Box display="flex" gap={1} alignItems="center">
                  <Chip
                    icon={getScopeIcon(a.assignment_scope)}
                    label={getScopeLabel(a.assignment_scope)}
                    size="small"
                    variant="outlined"
                    color={a.assignment_scope.includes('creators') ? 'secondary' : 'default'}
                  />
                  <Chip
                    label={`${a.signature_stats?.signed + (a.signature_stats?.agreed || 0)}/${a.signature_stats?.total}`}
                    color={a.signature_stats?.completion_rate === 100 ? 'success' : 'info'}
                    size="small"
                  />
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Message:</strong> {a.assignment_message || 'No message'}
                  </Typography>
                  {a.due_date && (
                    <Typography variant="body2" gutterBottom>
                      <strong>Due Date:</strong> {new Date(a.due_date).toLocaleDateString()}
                    </Typography>
                  )}
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">Completion Progress</Typography>
                      <Typography variant="body2">{a.signature_stats?.completion_rate?.toFixed(1)}%</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={a.signature_stats?.completion_rate || 0}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Chip icon={<SignedIcon />} label={`${a.signature_stats?.signed} Signed`} color="success" size="small" />
                    {a.signature_stats?.agreed > 0 && (
                      <Chip icon={<SignedIcon />} label={`${a.signature_stats?.agreed} Agreed`} color="info" size="small" />
                    )}
                    <Chip icon={<PendingIcon />} label={`${a.signature_stats?.pending} Pending`} color="warning" size="small" />
                    <Chip icon={<DeclinedIcon />} label={`${a.signature_stats?.declined} Declined`} color="error" size="small" />
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Button
                      variant="outlined"
                      startIcon={<RemindIcon />}
                      onClick={() => handleSendReminders(a.id)}
                      disabled={a.signature_stats?.pending === 0}
                      size="small"
                    >
                      Send Reminders ({a.signature_stats?.pending})
                    </Button>
                    <Button variant="outlined" startIcon={<ViewIcon />} size="small">
                      View Details
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<LinkIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenPublicLink(a.id);
                      }}
                    >
                      Public Link
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))
      ) : (
        <Card><CardContent>
          <Box textAlign="center" py={8}>
            <AssignIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" gutterBottom>No Contract Assignments</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {templates.filter(t => t.status === 'active').length === 0 
                ? 'Create some templates first, then assign them to users, teams, companies, or creators'
                : 'Assign contracts to users, teams, companies, or creators to start collecting signatures'}
            </Typography>
            <Box display="flex" gap={2} justifyContent="center">
              <Button
                variant="contained"
                startIcon={<AssignIcon />}
                onClick={onAssignContract}
                disabled={templates.filter(t => t.status === 'active').length === 0}
              >
                {templates.filter(t => t.status === 'active').length === 0 ? 'Create Templates First' : 'Create Assignment'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<BlitzPayIcon />}
                onClick={onAssignBlitzPay}
                disabled={templates.filter(t => t.status === 'active').length === 0}
              >
                BlitzPay Terms
              </Button>
            </Box>
          </Box>
        </CardContent></Card>
      )}
    </Box>
  );
};

const ContractAssignmentDialog = ({ open, templates, onClose, onAssign }) => {
  const [formData, setFormData] = useState({
    template_id: '',
    scope: 'company',
    company_id: '',
    team_id: '',
    user_id: '',
    creator_usernames: [],
    due_date: '',
    message: '',
    is_mandatory: true,
    auto_remind: true,
    reminder_frequency_days: 7
  });

  const [companies, setCompanies] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState({
    companies: false, teams: false, users: false, creators: false, submitting: false
  });

  const COMPANIES_PAGE_SIZE = 50;
  const [companiesPagination, setCompaniesPagination] = useState({
    total: null,
    hasMore: true,
  });
  const [isLoadingMoreCompanies, setIsLoadingMoreCompanies] = useState(false);

  useEffect(() => {
    if (open) {
      fetchOptions({ isInitial: true, append: false, offset: 0 });
    }
  }, [open]);
  useEffect(() => {
    if (formData.scope === 'team' && formData.company_id) fetchTeams(formData.company_id);
    if (formData.scope === 'user' && formData.company_id) fetchUsers(formData.company_id);
    if (formData.scope === 'creators_specific') fetchCreators();
  }, [formData.scope, formData.company_id]);

  const fetchOptions = async ({ isInitial = false, append = false, offset = 0 } = {}) => {
    try {
      if (isInitial) {
        setLoading(p => ({ ...p, companies: true }));
      } else {
        setIsLoadingMoreCompanies(true);
      }

      const response = await client.companies.list({
        limit: COMPANIES_PAGE_SIZE,
        offset,
      });

      const pageCompanies = Array.isArray(response)
        ? response
        : response?.companies || [];

      setCompanies(prev =>
        append ? [...prev, ...pageCompanies] : pageCompanies
      );

      const totalFromApi = typeof response?.total === 'number' ? response.total : null;
      const pageHasMore =
        pageCompanies.length === COMPANIES_PAGE_SIZE &&
        (totalFromApi == null || offset + pageCompanies.length < totalFromApi);

      setCompaniesPagination({
        total: totalFromApi,
        hasMore: pageHasMore,
      });
    } catch (error) {
      console.error('Failed to fetch companies:', error);
      if (!append) {
        setCompanies([]);
      }
    } finally {
      if (isInitial) {
        setLoading(p => ({ ...p, companies: false }));
      } else {
        setIsLoadingMoreCompanies(false);
      }
    }
  };

  const handleCompanyMenuScroll = (event) => {
    const list = event.currentTarget;
    if (!list) return;

    const nearBottom =
      list.scrollTop + list.clientHeight >= list.scrollHeight - 64;

    if (!nearBottom) return;
    if (loading.companies || isLoadingMoreCompanies) return;
    if (!companiesPagination.hasMore) return;

    const nextOffset = companies.length;
    fetchOptions({ isInitial: false, append: true, offset: nextOffset });
  };

  const fetchTeams = async (companyId) => {
    try {
      setLoading(p => ({ ...p, teams: true }));
      const teamsData = await client.companies.teams.list(companyId);
      const teamsArray = Array.isArray(teamsData)
        ? teamsData
        : teamsData?.teams || [];
      setTeams(teamsArray);
    } catch (error) {
      console.error('Failed to fetch teams:', error); setTeams([]);
    } finally { setLoading(p => ({ ...p, teams: false })); }
  };

  const fetchUsers = async (companyId) => {
    try {
      setLoading(p => ({ ...p, users: true }));
      const res = await client.companies.getUsers(companyId);
      const usersArray = Array.isArray(res) ? res : res.users || [];
      setUsers(usersArray);
    } catch (err) {
      console.error('Failed to fetch users:', err); setUsers([]);
    } finally { setLoading(p => ({ ...p, users: false })); }
  };

  const fetchCreators = async () => {
    try {
      setLoading(p => ({ ...p, creators: true }));
      const creatorsData = await client.creators.list({ is_vendor: true });
      const creatorsArray = Array.isArray(creatorsData) ? creatorsData : creatorsData?.creators || [];
      setCreators(creatorsArray.map(creator => ({
        ...creator,
        label: creator.creator || creator.username,
        value: creator.creator || creator.username
      })));
    } catch (error) {
      console.error('Failed to fetch creators:', error); setCreators([]);
    } finally { setLoading(p => ({ ...p, creators: false })); }
  };

  const handleScopeChange = (scope) => {
    setFormData(prev => ({ ...prev, scope, team_id: '', user_id: '', creator_usernames: [] }));
    if (scope !== 'team') setTeams([]);
    if (scope !== 'user') setUsers([]);
    if (scope !== 'creators_specific') setCreators([]);
  };
  const handleCompanyChange = (company_id) => {
    setFormData(prev => ({ ...prev, company_id, team_id: '', user_id: '' }));
    setTeams([]); setUsers([]);
  };

  const handleSubmit = async () => {
    if (!formData.template_id || !formData.scope) {
      alert('Template and scope are required');
      return;
    }
    
    if (['company', 'team', 'user'].includes(formData.scope) && !formData.company_id) {
      alert('Company selection is required');
      return;
    }
    if (formData.scope === 'team' && !formData.team_id) return alert('Team selection is required');
    if (formData.scope === 'user' && !formData.user_id) return alert('User selection is required');
    if (formData.scope === 'creators_specific' && formData.creator_usernames.length === 0) {
      return alert('At least one creator must be selected');
    }

    try {
      setLoading(p => ({ ...p, submitting: true }));
      await client.contracts.assignContract(formData);
      onAssign(); onClose();
      setFormData({
        template_id: '', scope: 'company', company_id: '', team_id: '', user_id: '', creator_usernames: [],
        due_date: '', message: '', is_mandatory: true, auto_remind: true, reminder_frequency_days: 7
      });
    } catch (error) {
      console.error('Failed to assign contract:', error);
      alert('Failed to assign contract');
    } finally {
      setLoading(p => ({ ...p, submitting: false }));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Assign Contract</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Contract Template</InputLabel>
              <Select
                label="Contract Template"
                value={formData.template_id}
                onChange={(e) => setFormData(p => ({ ...p, template_id: e.target.value }))}
                disabled={loading.submitting}
              >
                {templates.filter(t => t.status === 'active').map(t => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <Typography variant="subtitle1" gutterBottom>Assignment Scope</Typography>
              <RadioGroup
                value={formData.scope}
                onChange={(e) => handleScopeChange(e.target.value)}
                disabled={loading.submitting}
              >
                <FormControlLabel value="company" control={<Radio />} label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <CompanyIcon fontSize="small" />
                    Entire Company
                  </Box>
                } />
                <FormControlLabel value="team" control={<Radio />} label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <TeamIcon fontSize="small" />
                    Specific Team
                  </Box>
                } />
                <FormControlLabel value="user" control={<Radio />} label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <UserIcon fontSize="small" />
                    Individual User
                  </Box>
                } />
                <FormControlLabel value="creators_all" control={<Radio />} label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <CreatorIcon fontSize="small" />
                    All Creators
                  </Box>
                } />
                <FormControlLabel value="creators_specific" control={<Radio />} label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonAddIcon fontSize="small" />
                    Specific Creators
                  </Box>
                } />
              </RadioGroup>
            </FormControl>
          </Grid>

          {['company', 'team', 'user'].includes(formData.scope) && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Company</InputLabel>
                <Select
                  label="Company"
                  value={formData.company_id}
                  onChange={(e) => handleCompanyChange(e.target.value)}
                  disabled={loading.submitting || (loading.companies && companies.length === 0)}
                  MenuProps={{
                    PaperProps: {
                      onScroll: handleCompanyMenuScroll,
                    },
                  }}
                >
                  {loading.companies && companies.length === 0
                    ? [...Array(3)].map((_, index) => (
                        <MenuItem key={`companies-skeleton-${index}`} disabled>
                          <Skeleton variant="text" width="80%" />
                        </MenuItem>
                      ))
                    : companies.map(c => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))
                  }
                  {isLoadingMoreCompanies &&
                    [...Array(3)].map((_, index) => (
                      <MenuItem key={`loading-skeleton-${index}`} disabled>
                        <Skeleton sx={{backgroundColor: 'grey.400'}} variant="text" width="100%" height={40} />
                      </MenuItem>
                    ))
                  }
                  {!isLoadingMoreCompanies && companiesPagination.hasMore && (
                    <MenuItem disabled>
                      Scroll to load more companies...
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
          )}

          {formData.scope === 'team' && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Team</InputLabel>
                <Select
                  label="Team"
                  value={formData.team_id}
                  onChange={(e) => setFormData(p => ({ ...p, team_id: e.target.value }))}
                  disabled={!formData.company_id || loading.teams || loading.submitting}
                >
                  {loading.teams
                    ? <MenuItem disabled>Loading teams...</MenuItem>
                    : teams.length === 0 && formData.company_id
                      ? <MenuItem disabled>No teams found</MenuItem>
                      : teams.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          )}

          {formData.scope === 'user' && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>User</InputLabel>
                <Select
                  label="User"
                  value={formData.user_id}
                  onChange={(e) => setFormData(p => ({ ...p, user_id: e.target.value }))}
                  disabled={!formData.company_id || loading.users || loading.submitting}
                >
                  {loading.users
                    ? <MenuItem disabled>Loading users...</MenuItem>
                    : users.length === 0 && formData.company_id
                      ? <MenuItem disabled>No users found</MenuItem>
                      : users.map(u => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.first_name} {u.last_name} ({u.email})
                        </MenuItem>
                      ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {formData.scope === 'creators_specific' && (
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={creators}
                getOptionLabel={(option) => option.label || option.creator || ''}
                value={creators.filter(c => formData.creator_usernames.includes(c.value))}
                onChange={(e, newValue) => {
                  const usernames = newValue.map(creator => creator.value);
                  setFormData(p => ({ ...p, creator_usernames: usernames }));
                }}
                disabled={loading.creators || loading.submitting}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Select Creators" 
                    placeholder={loading.creators ? "Loading creators..." : "Choose creators"}
                    required
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <CreatorIcon sx={{ mr: 1 }} />
                    {option.label}
                  </Box>
                )}
              />
            </Grid>
          )}

          {formData.scope === 'creators_all' && (
            <Grid item xs={12}>
              <Alert severity="info">
                This will assign the contract to all active creators in your system.
              </Alert>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Due Date"
              type="datetime-local"
              value={formData.due_date}
              onChange={(e) => setFormData(p => ({ ...p, due_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              disabled={loading.submitting}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Assignment Message"
              multiline
              rows={3}
              value={formData.message}
              onChange={(e) => setFormData(p => ({ ...p, message: e.target.value }))}
              helperText="Optional message to include with the contract assignment"
              disabled={loading.submitting}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading.submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading.submitting}>
          {loading.submitting ? <CircularProgress size={20} /> : 'Assign Contract'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const BlitzPayTermsDialog = ({ open, templates, onClose, onAssign }) => {
  const [formData, setFormData] = useState({
    template_id: '',
    creator_usernames: [],
    emails: [],
    assignToAll: true,
    assignToExternal: false,
    due_date: '',
    message: 'Please review and accept the BlitzPay Terms & Conditions',
    auto_remind: true,
    reminder_frequency_days: 7
  });

  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState({
    creators: false,
    submitting: false
  });

  useEffect(() => {
    if (open && !formData.assignToAll && !formData.assignToExternal) {
      fetchCreators();
    }
  }, [open, formData.assignToAll, formData.assignToExternal]);

  const fetchCreators = async () => {
    try {
      setLoading(p => ({ ...p, creators: true }));
      const creatorsData = await client.creators.list({ is_vendor: true });
      const creatorsArray = Array.isArray(creatorsData) ? creatorsData : creatorsData?.creators || [];
      setCreators(creatorsArray.map(creator => ({
        ...creator,
        label: creator.creator || creator.username,
        value: creator.creator || creator.username
      })));
    } catch (error) {
      console.error('Failed to fetch creators:', error);
      setCreators([]);
    } finally {
      setLoading(p => ({ ...p, creators: false }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.template_id) {
      alert('Please select a template');
      return;
    }
    if (!formData.assignToExternal) {
      if (!formData.assignToAll && formData.creator_usernames.length === 0) {
        alert('Please select at least one creator');
        return;
      }
    } else {
      if (formData.emails.length === 0) {
        alert('Please add at least one email');
        return;
      }
    }

    try {
      setLoading(p => ({ ...p, submitting: true }));
      
      let payload;
      if (!formData.assignToExternal) {
        payload = {
          template_id: formData.template_id,
          message: formData.message,
          due_date: formData.due_date || undefined,
          auto_remind: formData.auto_remind,
          reminder_frequency_days: formData.reminder_frequency_days
        };

        if (!formData.assignToAll) {
          payload.creator_usernames = formData.creator_usernames;
        }

        await client.contracts.assignBlitzPayTermsToCreators(payload);
      } else {
        const signers = formData.emails.map(email => ({
          signer_name: email.split('@')[0],
          signer_email: email
        }));
        await client.contracts.bulkCreateBlitzPayExternalSignatures({ signers });
      }
      
      onAssign();
      onClose();
      setFormData({
        template_id: '',
        creator_usernames: [],
        emails: [],
        assignToAll: true,
        assignToExternal: false,
        due_date: '',
        message: 'Please review and accept the BlitzPay Terms & Conditions',
        auto_remind: true,
        reminder_frequency_days: 7
      });
    } catch (error) {
      console.error('Failed to assign BlitzPay terms:', error);
      alert('Failed to assign BlitzPay terms');
    } finally {
      setLoading(p => ({ ...p, submitting: false }));
    }
  };

  const blitzPayTemplates = templates.filter(t => 
    t.status === 'active' && 
    (t.name.toLowerCase().includes('blitzpay') || 
     t.name.toLowerCase().includes('terms') || 
     t.name.toLowerCase().includes('payment'))
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <BlitzPayIcon color="primary" />
          Assign BlitzPay Terms
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>BlitzPay Template</InputLabel>
              <Select
                value={formData.template_id}
                onChange={(e) => setFormData(p => ({ ...p, template_id: e.target.value }))}
                disabled={loading.submitting}
              >
                {blitzPayTemplates.map(t => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
                {blitzPayTemplates.length === 0 && (
                  <MenuItem disabled>No BlitzPay templates found</MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.assignToExternal}
                  onChange={(e) => setFormData(p => ({ ...p, assignToExternal: e.target.checked, assignToAll: true, creator_usernames: [], emails: [] }))}
                  disabled={loading.submitting}
                />
              }
              label="Assign to External Emails (non-creators)"
            />
          </Grid>

          {!formData.assignToExternal ? (
            <>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.assignToAll}
                      onChange={(e) => setFormData(p => ({ ...p, assignToAll: e.target.checked, creator_usernames: [] }))}
                      disabled={loading.submitting}
                    />
                  }
                  label="Assign to all active creators"
                />
              </Grid>

              {!formData.assignToAll && (
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={creators}
                    getOptionLabel={(option) => option.label || option.creator || ''}
                    value={creators.filter(c => formData.creator_usernames.includes(c.value))}
                    onChange={(e, newValue) => {
                      const usernames = newValue.map(creator => creator.value);
                      setFormData(p => ({ ...p, creator_usernames: usernames }));
                    }}
                    disabled={loading.creators || loading.submitting}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Select Specific Creators" 
                        placeholder={loading.creators ? "Loading creators..." : "Choose creators"}
                        required
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <CreatorIcon sx={{ mr: 1 }} />
                        {option.label}
                      </Box>
                    )}
                  />
                </Grid>
              )}
            </>
          ) : (
            <Grid item xs={12}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={formData.emails}
                onChange={(e, newValue) => setFormData(p => ({ ...p, emails: newValue }))}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Add Emails"
                    placeholder="Press enter to add email"
                    helperText="Add multiple emails for bulk assignment"
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Due Date"
              type="datetime-local"
              value={formData.due_date}
              onChange={(e) => setFormData(p => ({ ...p, due_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              disabled={loading.submitting}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Assignment Message"
              multiline
              rows={3}
              value={formData.message}
              onChange={(e) => setFormData(p => ({ ...p, message: e.target.value }))}
              helperText="Message that will be sent with the BlitzPay terms"
              disabled={loading.submitting}
            />
          </Grid>

          <Grid item xs={12}>
            <Alert severity="info">
              <Typography variant="body2">
                <strong>BlitzPay Terms Assignment:</strong> This will send contract terms that must be accepted before payments through BlitzPay.
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading.submitting}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit} 
          disabled={loading.submitting || blitzPayTemplates.length === 0}
        >
          {loading.submitting ? <CircularProgress size={20} /> : 'Assign BlitzPay Terms'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ExternalSignatureDialog = ({ open, templates, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    template_id: '',
    signer_name: '',
    signer_email: '',
    signer_phone: '',
    expires_in_days: 30,
    metadata: {}
  });

  const handleSubmit = async () => {
    if (!formData.template_id || !formData.signer_name || !formData.signer_email) {
      alert('Template, name, and email are required');
      return;
    }

    try {
      await client.contracts.createExternalSignature(formData);
      onSubmit();
      onClose();
      setFormData({
        template_id: '',
        signer_name: '',
        signer_email: '',
        signer_phone: '',
        expires_in_days: 30,
        metadata: {}
      });
    } catch (error) {
      console.error('Failed to create external signature:', error);
      alert('Failed to create external signature');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create External Signature Request</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Template</InputLabel>
              <Select
                value={formData.template_id}
                onChange={(e) => setFormData(p => ({ ...p, template_id: e.target.value }))}
              >
                {templates.filter(t => t.status === 'active').map(t => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Signer Name"
              value={formData.signer_name}
              onChange={(e) => setFormData(p => ({ ...p, signer_name: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Signer Email"
              type="email"
              value={formData.signer_email}
              onChange={(e) => setFormData(p => ({ ...p, signer_email: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Signer Phone (optional)"
              value={formData.signer_phone}
              onChange={(e) => setFormData(p => ({ ...p, signer_phone: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Expires in Days"
              type="number"
              value={formData.expires_in_days}
              onChange={(e) => setFormData(p => ({ ...p, expires_in_days: e.target.value }))}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>Create Request</Button>
      </DialogActions>
    </Dialog>
  );
};

const ExternalContractsAdmin = ({
  templates, externalSignatures, externalStats, onCreateExternal, onAssignBlitzPay,
  searchEmail, onSearchEmailChange, onSearchSubmit, onCopyUrl, loading
}) => {
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">External Contracts ({externalSignatures.length})</Typography>
        <Box display="flex" gap={2}>
          <Button variant="contained" startIcon={<PersonAddIcon />} onClick={onCreateExternal}>
            Create External Request
          </Button>
          <Button variant="outlined" startIcon={<BlitzPayIcon />} onClick={onAssignBlitzPay}>
            Bulk BlitzPay External
          </Button>
        </Box>
      </Box>

      <Box mb={3}>
        <TextField
          fullWidth
          label="Search by Email"
          value={searchEmail}
          onChange={onSearchEmailChange}
          onKeyPress={(e) => e.key === 'Enter' && onSearchSubmit()}
          InputProps={{
            endAdornment: (
              <IconButton onClick={onSearchSubmit}>
                <SendIcon />
              </IconButton>
            )
          }}
        />
      </Box>

      {externalStats && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} sm={3}>
            <Card><CardContent>
              <Typography color="textSecondary" variant="body2">Total External</Typography>
              <Typography variant="h4">{externalStats.total}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card><CardContent>
              <Typography color="textSecondary" variant="body2">Signed</Typography>
              <Typography variant="h4" color="success.main">{externalStats.signed}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card><CardContent>
              <Typography color="textSecondary" variant="body2">Pending</Typography>
              <Typography variant="h4" color="warning.main">{externalStats.pending}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card><CardContent>
              <Typography color="textSecondary" variant="body2">Completion Rate</Typography>
              <Typography variant="h4" color="primary.main">{externalStats.completion_rate?.toFixed(1)}%</Typography>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Signer Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Template</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {externalSignatures.map(sig => (
              <TableRow key={sig.id}>
                <TableCell>{sig.signer_name}</TableCell>
                <TableCell>{sig.signer_email}</TableCell>
                <TableCell>{sig.template?.name}</TableCell>
                <TableCell>
                  <Chip 
                    label={sig.status}
                    color={sig.status === 'signed' ? 'success' : sig.status === 'pending' ? 'warning' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{new Date(sig.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Box display="flex" gap={1}>
                    <Tooltip title="Copy Public URL">
                      <IconButton onClick={() => onCopyUrl(sig.public_url)}>
                        <CopyIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View Details">
                      <IconButton onClick={() => window.open(sig.public_url, '_blank')}>
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// NEW COMPONENT: Signed BlitzPay RPAs
const SignedBlitzPayRPAs = ({ 
  signedRPAs, 
  rpaStats, 
  filters, 
  onFiltersChange, 
  loading,
  onRefresh 
}) => {
  const [expandedCreator, setExpandedCreator] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const handleExport = async (format = 'csv') => {
    try {
      const data = await client.contracts.blitzpayRpa.exportSignedRPAs({ 
        format,
        creator_username: filters.creator_username 
      });
      
      if (format === 'csv') {
        const blob = new Blob([data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `blitzpay_rpa_signed_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
      }
    } catch (error) {
      console.error('Failed to export:', error);
      alert('Failed to export data');
    }
  };

  const filteredCreators = signedRPAs.creators?.filter(creator => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      creator.creator_username.toLowerCase().includes(query) ||
      creator.creator.display_name?.toLowerCase().includes(query) ||
      creator.creator.email?.toLowerCase().includes(query)
    );
  }) || [];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">
          Signed BlitzPay RPAs ({signedRPAs.summary?.total_creators || 0} Creators)
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<CopyIcon />}
            onClick={() => handleExport('csv')}
          >
            Export CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<BlitzPayIcon />}
            onClick={onRefresh}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </Box>
      </Box>

      {/* Stats Summary */}
      {rpaStats && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" variant="body2">Total Signed</Typography>
                <Typography variant="h4">{rpaStats.total_signed}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" variant="body2">External Signatures</Typography>
                <Typography variant="h4">{rpaStats.external_signatures}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" variant="body2">Internal Signatures</Typography>
                <Typography variant="h4">{rpaStats.internal_signatures}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" variant="body2">Recent (7 days)</Typography>
                <Typography variant="h4">{rpaStats.recent_7_days}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search Creators"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Username, name, or email"
                InputProps={{
                  startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.include_external}
                      onChange={(e) => onFiltersChange({ 
                        ...filters, 
                        include_external: e.target.checked 
                      })}
                    />
                  }
                  label="Include External"
                />
              </FormGroup>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.include_internal}
                      onChange={(e) => onFiltersChange({ 
                        ...filters, 
                        include_internal: e.target.checked 
                      })}
                    />
                  }
                  label="Include Internal"
                />
              </FormGroup>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Limit</InputLabel>
                <Select
                  value={filters.limit}
                  onChange={(e) => onFiltersChange({ ...filters, limit: e.target.value })}
                >
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Creators List */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : filteredCreators.length > 0 ? (
        <Box>
          {filteredCreators.map((creatorData) => (
            <Accordion 
              key={creatorData.creator_username}
              expanded={expandedCreator === creatorData.creator_username}
              onChange={() => setExpandedCreator(
                expandedCreator === creatorData.creator_username 
                  ? null 
                  : creatorData.creator_username
              )}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" width="100%" gap={2}>
                  <CreatorIcon color="secondary" />
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {creatorData.creator.display_name || creatorData.creator_username}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      @{creatorData.creator_username} • {creatorData.creator.email}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1} alignItems="center">
                    <Chip
                      label={`${creatorData.total_signatures} signature${creatorData.total_signatures !== 1 ? 's' : ''}`}
                      color="success"
                      size="small"
                    />
                    {creatorData.creator.creator_status === 'active' ? (
                      <Chip label="Active" color="success" size="small" variant="outlined" />
                    ) : (
                      <Chip label="Inactive" color="default" size="small" variant="outlined" />
                    )}
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  {/* Creator Details */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>Creator Information</Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText 
                          primary="Legal Entity Name"
                          secondary={creatorData.creator.legal_entity_name || 'N/A'}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Email"
                          secondary={creatorData.creator.email}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Payout Email"
                          secondary={creatorData.creator.payout_email || 'N/A'}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Phone"
                          secondary={creatorData.creator.phone_number || 'N/A'}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Onboarding"
                          secondary={creatorData.creator.onboarding_completed ? 'Completed' : 'Incomplete'}
                        />
                      </ListItem>
                      {creatorData.latest_signature_date && (
                        <ListItem>
                          <ListItemText 
                            primary="Latest Signature"
                            secondary={new Date(creatorData.latest_signature_date).toLocaleString()}
                          />
                        </ListItem>
                      )}
                    </List>
                  </Grid>

                  {/* Signatures */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Signatures ({creatorData.total_signatures})
                    </Typography>
                    
                    {/* External Signatures */}
                    {creatorData.signatures.external.length > 0 && (
                      <Box mb={2}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          External Signatures ({creatorData.signatures.external.length})
                        </Typography>
                        {creatorData.signatures.external.map((sig) => (
                          <Card key={sig.signature_id} variant="outlined" sx={{ mb: 1, p: 1 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Box>
                                <Typography variant="body2">
                                  <strong>Email:</strong> {sig.signer_email}
                                </Typography>
                                {sig.signer_phone && (
                                  <Typography variant="caption" color="text.secondary">
                                    Phone: {sig.signer_phone}
                                  </Typography>
                                )}
                                <Typography variant="caption" display="block" color="text.secondary">
                                  Signed: {new Date(sig.signed_at).toLocaleString()}
                                </Typography>
                              </Box>
                              <Chip label="External" size="small" color="info" />
                            </Box>
                          </Card>
                        ))}
                      </Box>
                    )}

                    {/* Internal Signatures */}
                    {creatorData.signatures.internal.length > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          Internal Signatures ({creatorData.signatures.internal.length})
                        </Typography>
                        {creatorData.signatures.internal.map((sig) => (
                          <Card key={sig.signature_id} variant="outlined" sx={{ mb: 1, p: 1 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Box>
                                <Typography variant="body2">
                                  Global User ID: {sig.global_user_id}
                                </Typography>
                                <Typography variant="caption" display="block" color="text.secondary">
                                  Signed: {new Date(sig.signed_at).toLocaleString()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Status: {sig.status}
                                </Typography>
                              </Box>
                              <Chip label="Internal" size="small" color="success" />
                            </Box>
                          </Card>
                        ))}
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ) : (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <BlitzPayIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {searchQuery ? 'No Creators Found' : 'No Signed BlitzPay RPAs'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchQuery 
                  ? `No creators match "${searchQuery}"`
                  : 'BlitzPay RPA signatures will appear here once creators sign'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {signedRPAs.summary?.total_creators > filters.limit && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Button
            onClick={() => onFiltersChange({ 
              ...filters, 
              offset: Math.max(0, filters.offset - filters.limit) 
            })}
            disabled={filters.offset === 0}
          >
            Previous
          </Button>
          <Typography variant="body2" sx={{ mx: 2, alignSelf: 'center' }}>
            Showing {filters.offset + 1} - {Math.min(filters.offset + filters.limit, signedRPAs.summary.total_creators)} of {signedRPAs.summary.total_creators}
          </Typography>
          <Button
            onClick={() => onFiltersChange({ 
              ...filters, 
              offset: filters.offset + filters.limit 
            })}
            disabled={filters.offset + filters.limit >= signedRPAs.summary.total_creators}
          >
            Next
          </Button>
        </Box>
      )}
    </Box>
  );
};

const ContractAnalyticsAdmin = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => { fetchAnalytics(); }, []);
  
  const fetchAnalytics = async () => {
    try {
      const systemStats = await client.contracts.getSystemStats();
      setAnalytics(systemStats.stats);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally { 
      setLoading(false); 
    }
  };
  
  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card><CardContent>
          <Typography variant="h6" gutterBottom>System-wide Statistics</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Total Templates</Typography>
              <Typography variant="h4">{analytics?.total_templates}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Active Templates</Typography>
              <Typography variant="h4" color="success.main">{analytics?.active_templates}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Simple Contracts</Typography>
              <Typography variant="h4" color="info.main">{analytics?.simple_templates}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Complex Contracts</Typography>
              <Typography variant="h4" color="warning.main">{analytics?.complex_templates}</Typography>
            </Grid>
          </Grid>
        </CardContent></Card>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Card><CardContent>
          <Typography variant="h6" gutterBottom>Assignment Breakdown</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Total Assignments</Typography>
              <Typography variant="h4">{analytics?.total_assignments}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Simple Assignments</Typography>
              <Typography variant="h4" color="info.main">{analytics?.simple_assignments}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Complex Assignments</Typography>
              <Typography variant="h4" color="warning.main">{analytics?.complex_assignments}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Completion Rate</Typography>
              <Typography variant="h4" color="primary.main">{analytics?.completion_rate?.toFixed(1)}%</Typography>
            </Grid>
          </Grid>
        </CardContent></Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card><CardContent>
          <Typography variant="h6" gutterBottom>Signature Status Breakdown</Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Box textAlign="center">
                <SignedIcon color="success" fontSize="large" />
                <Typography variant="h5" color="success.main">{analytics?.signed_contracts}</Typography>
                <Typography variant="body2" color="text.secondary">Signed</Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box textAlign="center">
                <SignedIcon color="info" fontSize="large" />
                <Typography variant="h5" color="info.main">{analytics?.agreed_contracts}</Typography>
                <Typography variant="body2" color="text.secondary">Agreed</Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box textAlign="center">
                <PendingIcon color="warning" fontSize="large" />
                <Typography variant="h5" color="warning.main">{analytics?.pending_contracts}</Typography>
                <Typography variant="body2" color="text.secondary">Pending</Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent></Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card><CardContent>
          <Typography variant="h6" gutterBottom>Total Signatures</Typography>
          <Box textAlign="center">
            <Typography variant="h2" color="primary.main" sx={{ mb: 2 }}>
              {analytics?.total_signatures}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={analytics?.completion_rate || 0} 
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {analytics?.completed_contracts} completed out of {analytics?.total_signatures}
            </Typography>
          </Box>
        </CardContent></Card>
      </Grid>
    </Grid>
  );
};

const PendingSignaturesAdmin = () => {
  const [pendingSignatures, setPendingSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);

  useEffect(() => { fetchPendingSignatures(); }, []);
  
  const fetchPendingSignatures = async () => {
    try {
      const assignments = await client.contracts.getAssignments();
      const pendingAssignments = assignments.assignments?.filter(
        a => a.signature_stats?.pending > 0
      ) || [];
      setPendingSignatures(pendingAssignments);
    } catch (error) {
      console.error('Failed to fetch pending signatures:', error);
    } finally { 
      setLoading(false); 
    }
  };

  const handleSendReminders = async (assignmentId) => {
    try {
      setSendingId(assignmentId);
      const result = await client.contracts.sendReminders(assignmentId);
      alert(`Sent ${result.reminder_count} reminders successfully`);
      fetchPendingSignatures();
    } catch (error) {
      console.error('Failed to send reminders:', error);
      alert('Failed to send reminders');
    } finally {
      setSendingId(null);
    }
  };

  const getScopeIcon = (scope) => {
    switch (scope) {
      case 'company': return <CompanyIcon />;
      case 'team': return <TeamIcon />;
      case 'user': return <UserIcon />;
      case 'creators_all':
      case 'creators_specific': return <CreatorIcon />;
      default: return <AssignIcon />;
    }
  };

  const getScopeLabel = (scope) => {
    switch (scope) {
      case 'creators_all': return 'All Creators';
      case 'creators_specific': return 'Specific Creators';
      default: return scope;
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Assignments with Pending Signatures ({pendingSignatures.length})
      </Typography>
      {pendingSignatures.map(a => (
        <Card key={a.id} sx={{ mb: 2 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="start">
              <Box>
                <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                  {getScopeIcon(a.assignment_scope)}
                  {a.template?.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {getScopeLabel(a.assignment_scope)} assignment
                </Typography>
                <Typography variant="body2">
                  {a.signature_stats?.pending} pending signatures out of {a.signature_stats?.total}
                </Typography>
                {a.due_date && (
                  <Typography variant="body2" color="warning.main">
                    Due: {new Date(a.due_date).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
              <Box display="flex" flexDirection="column" gap={1} alignItems="end">
                <Chip
                  icon={getScopeIcon(a.assignment_scope)}
                  label={`${a.signature_stats?.pending} pending`}
                  color="warning"
                  size="small"
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RemindIcon />}
                  disabled={sendingId === a.id}
                  onClick={() => handleSendReminders(a.id)}
                >
                  {sendingId === a.id ? 'Sending...' : 'Send Reminders'}
                </Button>
              </Box>
            </Box>
            <Box sx={{ mt: 2 }}>
              <LinearProgress
                variant="determinate"
                value={a.signature_stats?.completion_rate || 0}
                sx={{ height: 6, borderRadius: 3 }}
              />
              <Typography variant="caption" color="text.secondary">
                {a.signature_stats?.completion_rate?.toFixed(1)}% complete
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ))}

      {pendingSignatures.length === 0 && (
        <Card><CardContent>
          <Box textAlign="center" py={4}>
            <SignedIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6" gutterBottom>All contracts are up to date!</Typography>
            <Typography variant="body2" color="text.secondary">
              No pending signatures require attention.
            </Typography>
          </Box>
        </CardContent></Card>
      )}
    </Box>
  );
};

export default ContractDetailsView;